import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Flex } from 'components/ui';
import CompareZoneBase from 'components/Catalog/ProductGrid/CompareModal/Zones/CompareZoneBase';
import bpRedesignedUtils from 'utils/BeautyPreferencesRedesigned';
import plpCompareUtils from 'utils/plpCompare';
import { space } from 'style/config';
import EmptyState from 'components/Catalog/ProductGrid/CompareModal/EmptyState';
import { zoneStyles } from 'components/Catalog/ProductGrid/CompareModal/Zones/zoneStyles';

/**
 * Parse attribute configuration
 * Handles both object format: { labelKey: ['attrKey1', 'attrKey2'] }
 * And string format: 'attrKey'
 */
const parseAttributeConfig = attr => {
    if (typeof attr === 'string') {
        return {
            labelKey: attr,
            attributeKeys: [attr]
        };
    }

    if (typeof attr === 'object' && attr !== null) {
        const keys = Object.keys(attr);

        if (keys.length > 0) {
            const labelKey = keys[0];
            const attributeKeys = attr[labelKey];

            return {
                labelKey,
                attributeKeys: Array.isArray(attributeKeys) ? attributeKeys : [attributeKeys]
            };
        }
    }

    return null;
};

/**
 * Get refinement from masterlistWorld by key
 */
const getRefinement = (masterlistWorld, refinementKey) => {
    if (!masterlistWorld?.refinements) {
        return null;
    }

    return masterlistWorld.refinements.find(ref => ref?.key === refinementKey);
};

/**
 * Map facet values to display names using refinement items
 */
const mapFacetValues = (facetValues, refinement) => {
    if (!refinement?.items) {
        return [];
    }

    return facetValues.map(facet => refinement.items.find(item => item.key === facet)?.value).filter(value => value != null);
};

/**
 * Extracts attribute values from a SKU based on attribute keys
 * Maps facet values to their display names using masterList refinements
 */
const extractAttributeValues = (masterlistWorld, sku, attributeKeys) => {
    if (attributeKeys.length === 0 || !sku?.skuRefinements) {
        return [];
    }

    const allValues = [];

    attributeKeys.forEach(attributeKey => {
        const skuRefinement = sku.skuRefinements.find(ref => ref?.key === attributeKey);

        if (skuRefinement?.values) {
            const masterListRefinement = getRefinement(masterlistWorld, attributeKey);

            if (masterListRefinement) {
                const mappedValues = mapFacetValues(skuRefinement.values, masterListRefinement);
                allValues.push(...mappedValues);
            } else {
                // If no master list refinement found, use raw values
                allValues.push(...skuRefinement.values);
            }
        }
    });

    return allValues;
};

const getProductWorldInfo = product => {
    const parentCategoryId = product?.parentProducts?.[0]?.parentCategory?.ancestorCategories?.[0]?.categoryKey;
    let masterlistWorld;
    let plpCompareAttributes;

    // Bath and Body is not part of master list refinements and will require extensive changes to
    // existing implementations to support, so we are hardcoding it to use skincare master list refinements.

    // Hardcode bathAndBody catId
    if (parentCategoryId === 'cat140014') {
        // Hardcode skinCare catId
        masterlistWorld = bpRedesignedUtils.getMasterListRefinementWorldByCategoryId('cat150006');
        // Hardcode Bath & Body recConfig key
        plpCompareAttributes = plpCompareUtils.getPlpCompareAttributes('bathAndBody');
    } else {
        masterlistWorld = bpRedesignedUtils.getMasterListRefinementWorldByCategoryId(parentCategoryId);
        plpCompareAttributes = plpCompareUtils.getPlpCompareAttributes(masterlistWorld?.key);
    }

    return { masterlistWorld, plpCompareAttributes };
};

const parseProductAttributes = (product, getText) => {
    const { masterlistWorld, plpCompareAttributes } = getProductWorldInfo(product);

    if (!masterlistWorld || !plpCompareAttributes || plpCompareAttributes.length === 0) {
        return [];
    }

    const parsedAttributes = plpCompareAttributes
        .map(attr => {
            const config = parseAttributeConfig(attr);

            if (!config) {
                return null;
            }

            const values = extractAttributeValues(masterlistWorld, product, config.attributeKeys);
            const refinement = getRefinement(masterlistWorld, config.labelKey);
            const label = refinement?.value || getText(config.labelKey);

            // Always include attributes, even if they have no values
            return { config, values, label, isEmpty: values.length === 0 };
        })
        .filter(Boolean);

    return parsedAttributes;
};

/**
 * Builds a unified ordered list of all attribute keys across all products,
 * then maps each product's attributes to that unified list so the same
 * attribute type always appears in the same row regardless of which products
 * have it. Rows where no real product has a value are excluded.
 */
const buildAlignedAttributes = (allProductAttributes, skus) => {
    const seenKeys = new Set();
    const orderedRows = [];

    allProductAttributes.forEach((productAttrs, index) => {
        if (!skus[index]) {
            return;
        }

        productAttrs.forEach(attr => {
            if (!seenKeys.has(attr.config.labelKey)) {
                seenKeys.add(attr.config.labelKey);
                orderedRows.push({ labelKey: attr.config.labelKey, label: attr.label });
            }
        });
    });

    const attrMaps = allProductAttributes.map(productAttrs => {
        const attrByKey = {};
        productAttrs.forEach(attr => {
            attrByKey[attr.config.labelKey] = attr;
        });

        return attrByKey;
    });

    // Only keep rows where at least one real product has a non-empty value
    const filteredRows = orderedRows.filter(({ labelKey }) =>
        attrMaps.some((attrByKey, prodIndex) => {
            if (!skus[prodIndex]) {
                return false;
            }

            const attr = attrByKey[labelKey];

            return attr && !attr.isEmpty;
        })
    );

    const alignedAttributes = attrMaps.map(attrByKey => filteredRows.map(({ labelKey }) => attrByKey[labelKey] ?? null));

    return { alignedAttributes, orderedRows: filteredRows };
};

const AttributeGrid = ({ allProductAttributes, orderedRows, skus, getText }) => {
    return (
        <Flex
            flexDirection='column'
            gap={4}
        >
            {orderedRows.map(({ labelKey, label }, rowIndex) => (
                <Box
                    key={`attribute-row-${labelKey}`}
                    css={zoneStyles.productsContainer}
                >
                    {allProductAttributes.map((productAttrs, productIndex) => {
                        const hasProduct = !!skus[productIndex];
                        const attribute = productAttrs[rowIndex];
                        const isEmpty = !attribute || attribute.isEmpty;

                        return (
                            <Box
                                key={`product-${productIndex}-attr-${labelKey}`}
                                css={zoneStyles.productSlot}
                                role='group'
                                aria-label={hasProduct ? getText('productInformation', [productIndex + 1]) : undefined}
                            >
                                {hasProduct && (
                                    <Box css={zoneStyles.zoneContent}>
                                        <Flex flexDirection='column'>
                                            <Text
                                                is='h3'
                                                fontSize='sm'
                                                color='gray'
                                            >
                                                {label || labelKey}:
                                            </Text>
                                            {isEmpty ? (
                                                <EmptyState />
                                            ) : (
                                                <Box
                                                    is='ul'
                                                    css={styles.attributeList}
                                                >
                                                    {attribute.values.map((value, valIndex) => (
                                                        <Text
                                                            key={`${labelKey}-${value}-${valIndex}`}
                                                            is='li'
                                                            fontSize='base'
                                                            lineHeight='tight'
                                                            paddingLeft={1}
                                                        >
                                                            {value}
                                                        </Text>
                                                    ))}
                                                </Box>
                                            )}
                                        </Flex>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Flex>
    );
};

const OverviewZone = ({ skus = [], getText = () => {} }) => {
    const allProductAttributes = skus.map(product => {
        if (!product) {
            return [];
        }

        return parseProductAttributes(product, getText);
    });

    const { alignedAttributes, orderedRows } = buildAlignedAttributes(allProductAttributes, skus);

    if (orderedRows.length === 0) {
        return (
            <CompareZoneBase
                skus={skus}
                getText={getText}
                titleKey='overview'
                renderContent={() => <EmptyState />}
            />
        );
    }

    return (
        <CompareZoneBase
            skus={skus}
            getText={getText}
            titleKey='overview'
            customContent={
                <AttributeGrid
                    allProductAttributes={alignedAttributes}
                    orderedRows={orderedRows}
                    skus={skus}
                    getText={getText}
                />
            }
        />
    );
};

const styles = {
    attributeList: {
        listStyleType: 'disc',
        paddingLeft: `${space[4]}px`,
        marginTop: `${space[1]}px`,
        'li::marker': {
            fontSize: '0.7em'
        }
    }
};

OverviewZone.propTypes = {
    skus: PropTypes.arrayOf(PropTypes.object),
    getText: PropTypes.func
};

export default wrapFunctionalComponent(OverviewZone, 'OverviewZone');
